import { CanMatchFn, Route, UrlSegment } from '@angular/router';

import { AppRoute, buildUrl } from './app-routes.enum';

/**
 * v2-share-routes — the SINGLE SOURCE OF TRUTH for every CANONICAL, SHAREABLE
 * resource url of the primary application.
 *
 * A share url is NOT a redirect target or a special "landing" page: it is a
 * first-class application route that renders the very same surface, facade,
 * skeletons and error states as tapping the resource from inside the app. The
 * shapes are:
 *
 *   /care/provider/{id}      → Care CR3   provider detail
 *   /care/program/{id}       → Care CR12  program detail
 *   /care/group/{id}         → Care CR14  support-group detail
 *   /community/post/{id}     → COMM2      post detail
 *   /library/article/{id}    → COMM7      reader (article)
 *   /library/podcast/{id}    → COMM7      reader (podcast)
 *   /library/meditation/{id} → COMM7      reader (meditation)
 *
 * ONE table ({@link SHARE_RESOURCE_BASE}) drives BOTH sides of the contract, so
 * the url a share button generates and the route that answers it can never
 * drift apart:
 *
 *   · route tables      → {@link shareRoutePath} + {@link SHARE_RESOURCE_ROUTE_DATA}
 *   · url generation    → {@link shareLinkPath} (and `ShareLinkService.urlFor`,
 *                          which makes it absolute against the RUNNING origin)
 *
 * Pure + SSR-safe: string work only, no platform, router or DI access — so it is
 * equally usable from a route table, a component computed, and the server bundle.
 */

/** Every resource addressable by a canonical, shareable url. */
export enum ShareResource {
  PROVIDER = 'provider',
  PROGRAM = 'program',
  GROUP = 'group',
  POST = 'post',
  ARTICLE = 'article',
  PODCAST = 'podcast',
  MEDITATION = 'meditation',
}

/**
 * Route-`data` key naming the {@link ShareResource} a route addresses. The
 * surface host reads it to decide WHICH by-id open to run, so a new shareable
 * resource is a table entry + a facade call — never a new page or a new route
 * component.
 */
export const SHARE_RESOURCE_ROUTE_DATA = 'shareResource';

/**
 * Route-`data` key naming the `?screen=` alias a route defaults to when the url
 * carries none — how `/library` lands on the Community library (COMM6) without
 * an intermediate redirect, reusing the host's existing SCREEN_MAP.
 */
export const DEFAULT_SCREEN_ROUTE_DATA = 'defaultScreen';

/** The route param carrying the entity id on every share route. */
export const SHARE_ID_PARAM = 'id';

/** Which top-level surface owns each resource's canonical url. */
const SHARE_RESOURCE_BASE: Readonly<Record<ShareResource, AppRoute>> = {
  [ShareResource.PROVIDER]: AppRoute.CARE,
  [ShareResource.PROGRAM]: AppRoute.CARE,
  [ShareResource.GROUP]: AppRoute.CARE,
  [ShareResource.POST]: AppRoute.COMMUNITY,
  [ShareResource.ARTICLE]: AppRoute.LIBRARY,
  [ShareResource.PODCAST]: AppRoute.LIBRARY,
  [ShareResource.MEDITATION]: AppRoute.LIBRARY,
};

/**
 * Is this a shareable entity id? Every shareable resource is keyed by a positive
 * integer backend id, so a local seed slug, a blank, `0`, or junk is NOT
 * addressable — {@link shareIdGuard} uses this to decline the route (→ the
 * branded 404) and {@link shareLinkPath} uses it to decline generating a link
 * that would 404 the recipient.
 */
export function isShareableId(raw: string | number | null | undefined): boolean {
  return /^[1-9]\d*$/.test(String(raw ?? '').trim());
}

/** The RELATIVE child path for a share route (`'provider/:id'`) — for route tables. */
export function shareRoutePath(resource: ShareResource): string {
  return `${resource}/:${SHARE_ID_PARAM}`;
}

/** The absolute root-relative path of a resource's surface (`'/care'`, `'/library'`). */
export function shareResourceBasePath(resource: ShareResource): string {
  return buildUrl(SHARE_RESOURCE_BASE[resource]);
}

/**
 * The canonical ROOT-RELATIVE path for a resource (`'/care/provider/27498'`), or
 * `null` when the id is not addressable ({@link isShareableId}) — callers then fall
 * back to the surface itself rather than minting a link that lands on the 404.
 */
export function shareLinkPath(
  resource: ShareResource,
  id: string | number | null | undefined,
): string | null {
  const value = String(id ?? '').trim();
  return isShareableId(value) ? buildUrl(SHARE_RESOURCE_BASE[resource], resource, value) : null;
}

/**
 * `canMatch` for every share route: the id segment must be a real backend id.
 *
 * Declining the MATCH (rather than activating and no-op'ing) is what makes
 * `/care/program/abc` fall through the route table to the app-level `**` and
 * render the EXISTING branded 404 — no redirect, no flash, no bespoke
 * not-found handling per surface, and identical on the server and after
 * hydration because it is a pure function of the url.
 */
export const shareIdGuard: CanMatchFn = (_route: Route, segments: readonly UrlSegment[]): boolean =>
  isShareableId(segments[segments.length - 1]?.path);

/**
 * The SERVER-SIDE half of {@link shareIdGuard} — "this url has the shape of a share
 * link, but its id is not addressable".
 *
 * `app.routes.server.ts` has to declare the share urls as `care/provider/**`,
 * `community/post/**`, `library/**` … because a `ServerRoute` path is a prefix
 * pattern and cannot carry the `[1-9]\d*` rule the guard applies. So `/care/program/abc`
 * MATCHED a share server route, which declares no `status`, and the render engine
 * answered `200 OK` — while the router, correctly, declined the match and rendered the
 * branded 404. That is the SOFT 404 the `**` entry in that file exists to eliminate,
 * leaking back in through the one family of urls listed ahead of it.
 *
 * Expressed here rather than in `src/server.ts` so the rule stays where the id rule
 * already lives: the predicate is {@link isShareableId} itself, and the shapes come
 * from the same {@link SHARE_RESOURCE_BASE} table that generates the links. A new
 * shareable resource is covered by adding its table entry, exactly as before.
 *
 * Pure string work on a root-relative pathname (no query, no origin), so it is safe
 * to call from the Express entry point, which runs outside the Angular injector.
 */
export function isUnaddressableShareUrl(pathname: string): boolean {
  const segments = pathname.split('?')[0]!.split('#')[0]!.split('/').filter(Boolean);
  // Every canonical share url is exactly `<surface>/<resource>/<id>`. Anything
  // longer or shorter is not a share url at all and already falls through to the
  // app-level `**`, which answers a real 404 on its own.
  if (segments.length !== 3) return false;

  const [surface, resource, id] = segments as [string, string, string];
  const base = SHARE_RESOURCE_BASE[resource as ShareResource];
  if (base === undefined || buildUrl(base) !== `/${surface}`) return false;

  return !isShareableId(id);
}
